import { lazy, Suspense } from 'react';
import { LandingPage } from './components/LandingPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { isElectron } from './utils/electron';
import { CriticalBanner } from './components/CriticalBanner';

import { DesktopDashboard } from './components/DesktopDashboard';
import { Overlay } from './components/Overlay';

// Lazy load static pages
const AboutPage = lazy(() => import('./components/AboutPage').then(m => ({ default: m.AboutPage })));
const DemoPage = lazy(() => import('./components/DemoPage').then(m => ({ default: m.DemoPage })));
const FAQPage = lazy(() => import('./components/FAQPage').then(m => ({ default: m.FAQPage })));
const GuidesHubPage = lazy(() => import('./components/GuidesHubPage').then(m => ({ default: m.GuidesHubPage })));
const GuidePage = lazy(() => import('./components/GuidePage').then(m => ({ default: m.GuidePage })));
const PrivacyPage = lazy(() => import('./components/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./components/TermsPage').then(m => ({ default: m.TermsPage })));

// Minimal loading fallback for lazy routes
const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Check if we're in overlay mode (has token, widgetToken, or test param in URL)
function isOverlayMode() {
  const search = window.location.search;
  const params = new URLSearchParams(search);

  // Checking both URLSearchParams and raw search string for maximum OBS reliability
  return (
    params.has('token') ||
    params.has('widgetToken') ||
    params.has('widget_token') ||
    params.has('test') ||
    search.includes('test=true') ||
    search.includes('widgetToken=')
  );
}

// Main App - routes between landing, overlay, and desktop
function App() {
  const path = window.location.pathname;
  const inOverlayMode = isOverlayMode();

  // If accessing /overlay OR has any valid overlay parameter, show overlay immediately
  // This is the absolute priority to ensure OBS sources render correctly
  if (path === '/overlay' || inOverlayMode) {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Overlay />
      </ErrorBoundary>
    );
  }

  // Static pages (available on web) - lazy loaded for smaller initial bundle
  if (path === '/about') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <AboutPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path === '/demo') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <DemoPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path === '/faq') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <FAQPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path === '/guides') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <GuidesHubPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path.startsWith('/guides/')) {
    const slug = path.slice('/guides/'.length).replace(/\/+$/, '');

    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <GuidePage slug={slug} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path === '/privacy') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <PrivacyPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (path === '/terms') {
    return (
      <ErrorBoundary>
        <CriticalBanner />
        <Suspense fallback={<PageLoader />}>
          <TermsPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Desktop app gets its own dedicated component (handles banner internally)
  if (isElectron()) {
    return (
      <ErrorBoundary>
        <DesktopDashboard />
      </ErrorBoundary>
    );
  }

  // Web gets the landing page
  return (
    <ErrorBoundary>
      <LandingPage />
    </ErrorBoundary>
  );
}

export default App;
